import { FC, useEffect } from 'react';

import { Paragraph } from '@purpur/library';

import { IMAGES_PATH } from '@telialt/assets/src/constants';
import { redirectToLogout, reloadPage } from '@telialt/utils/src/SSO/api';
import {
    SESSION_PROLONGATION_MODAL_BEFORE_END_TIME,
    SESSION_PROLONGATION_MODAL_CHECK_INTERVAL,
} from '@telialt/utils/src/SSO/constants';
import { useAutoProlongSession } from '@telialt/utils/src/SSO/useAutoProlongSession';
import { extendSessionExpireTime } from '@telialt/utils/src/SSO/utils';

import { SESSION_PROLONGATION_MODAL_TEST_ID } from './constants';
import {
    BodyWrapper,
    DestructiveTextButton,
    FooterButton,
    NarrowModal,
    TeliaBrandLogo,
} from './SessionProlongationModal.styles';
import { useSessionExpirations } from './useSessionExpirations';

const text = {
    windowIsNotUsed: 'Šis langas jau kurį laiką yra nenaudojamas.',
    secureLogout: 'Saugumo sumetimais, netrukus atsijungsime.',
    buttonContinue: 'Tęsti naršymą',
    buttonLogout: 'Atsijungti',
};

type Props = { beforeEndTime?: number; checkInterval?: number };

export const SessionProlongationModal: FC<Props> = ({
    beforeEndTime = SESSION_PROLONGATION_MODAL_BEFORE_END_TIME,
    checkInterval = SESSION_PROLONGATION_MODAL_CHECK_INTERVAL,
}) => {
    const { isNearExpire, isExpired, check } = useSessionExpirations(beforeEndTime, checkInterval);

    useEffect(() => {
        if (isExpired) {
            reloadPage();
        }
    }, [isExpired]);

    useAutoProlongSession();

    const handleContinueSessionClick = () => {
        // Extending session expiration for 1 minute while ping request is in progress
        extendSessionExpireTime(beforeEndTime + 60 * 1000);
        // Check session expiration, it should close modal
        check();
        // ping will be done by useAutoProlongSession click handler
    };

    return (
        <NarrowModal
            onHide={handleContinueSessionClick}
            isShown={isNearExpire}
            dataTest={SESSION_PROLONGATION_MODAL_TEST_ID.MODAL}
        >
            <BodyWrapper>
                <TeliaBrandLogo alt="Telia logotipas" src={`${IMAGES_PATH}/telia-logo.svg`} height={32} />
                <Paragraph>
                    {text.windowIsNotUsed}
                    <br />
                    {text.secureLogout}
                </Paragraph>
                <FooterButton
                    onClick={handleContinueSessionClick}
                    variant="primary"
                    data-test={SESSION_PROLONGATION_MODAL_TEST_ID.CONTINUE_BTN}
                >
                    {text.buttonContinue}
                </FooterButton>
                <DestructiveTextButton
                    onClick={redirectToLogout}
                    variant="text"
                    data-test={SESSION_PROLONGATION_MODAL_TEST_ID.LOG_OUT_BTN}
                >
                    {text.buttonLogout}
                </DestructiveTextButton>
            </BodyWrapper>
        </NarrowModal>
    );
};
